import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, Icon, IconBtn, Input, PageHeader, Tabs } from "../../design-system";
import {
  FORMACION_COURSES,
  fmtMinutes,
  progressOfCourse,
  totalVolumes,
} from "./data";
import { CourseThumb, ProgressBar } from "./atoms";

// Metadatos sintéticos de inscripción — última actividad, fecha de alta,
// certificado. UI-only mientras no exista el backend de progreso.
const HISTORIAL_META = {
  "prl-2026": { enrolled: "12 Ene 2026", lastAt: "Ayer · 16:42", cert: false },
  "rgpd-2026": { enrolled: "15 Ene 2026", lastAt: "Hace 3 días", cert: false },
  "kronaly-onboarding": { enrolled: "08 Sep 2025", lastAt: "21 Sep 2025", cert: true, completedOn: "21 Sep 2025" },
  "excel-rrhh": { enrolled: "04 Feb 2026", lastAt: "Hace 2 días", cert: false },
};

// Cursos completados en el pasado (ya no están en catálogo).
const PAST_COMPLETED = [
  { id: "compliance-2025", title: "Compliance anual 2025", category: "Compliance", durationMin: 90, completedOn: "12 Dic 2025", instructor: "Cabrera Legal", accent: "warning", rating: 4.4, type: "presentation" },
  { id: "ofimatica-basica", title: "Ofimática esencial: Word + Excel + Slides", category: "Habilidades", durationMin: 240, completedOn: "30 Oct 2025", instructor: "Roberto Salazar", accent: "pending", rating: 4.6, type: "video" },
  { id: "kronaly-novedades", title: "Kronaly · Novedades Q4 2025", category: "Producto", durationMin: 38, completedOn: "18 Oct 2025", instructor: "Ana Castro", accent: "brand", rating: 4.8, type: "video" },
];

function FormacionHistorial() {
  document.title = "Mi historial | Formación · Kronaly";
  const navigate = useNavigate();
  const onOpenCourse = (id) => navigate(`/formacion/${id}`);
  const onBack = () => navigate("/formacion");

  const [tab, setTab] = useState("en-progreso");
  const [query, setQuery] = useState("");

  const records = useMemo(() => {
    const items = [];
    FORMACION_COURSES.forEach((c) => {
      const meta = HISTORIAL_META[c.id];
      if (!c.enrolled) return;
      const p = progressOfCourse(c);
      items.push({
        kind: "course",
        course: c,
        status: p === 0 ? "apuntado" : p === 1 ? "completado" : "en-progreso",
        progress: p,
        enrolled: meta?.enrolled || "—",
        lastAt: meta?.lastAt || "—",
        cert: meta?.cert,
        completedOn: meta?.completedOn,
      });
    });
    PAST_COMPLETED.forEach((p) => {
      items.push({
        kind: "past",
        course: { ...p, mandatory: false, students: 0, enrolled: true, volumes: [] },
        status: "completado",
        progress: 1,
        enrolled: "—",
        lastAt: p.completedOn,
        cert: true,
        completedOn: p.completedOn,
      });
    });
    return items;
  }, []);

  const filteredByTab = records.filter((r) => (tab === "todos" ? true : r.status === tab));
  const filtered = filteredByTab.filter((r) => {
    if (!query) return true;
    const c = r.course;
    return `${c.title} ${c.instructor} ${c.category}`.toLowerCase().includes(query.toLowerCase());
  });

  const totalH = records.reduce((a, r) => a + (r.course.durationMin || 0) * r.progress, 0);
  const completed = records.filter((r) => r.status === "completado").length;
  const inProg = records.filter((r) => r.status === "en-progreso").length;
  const certs = records.filter((r) => r.cert).length;

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Aprendizaje · Mi historial"
        title="Mi historial"
        description="Todos los cursos a los que te has apuntado, su progreso y certificados."
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button variant="secondary" icon="Download">Exportar histórico</Button>
            <Button variant="primary" icon="BookOpen" onClick={onBack}>Ir al catálogo</Button>
          </div>
        }
      />

      {/* Fila de KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }} data-responsive-grid="4">
        <KpiH label="Total apuntados" value={records.length} />
        <KpiH label="Completados" value={completed} tone="success" />
        <KpiH label="En progreso" value={inProg} tone="brand" />
        <KpiH label="Tiempo total" value={Math.round(totalH / 60)} suffix="horas formado" />
      </div>

      <Card padded={false}>
        <div style={{ padding: "12px 16px 0" }}>
          <Tabs
            tabs={[
              { id: "en-progreso", label: "En progreso", count: records.filter((r) => r.status === "en-progreso").length },
              { id: "completado", label: "Completados", count: records.filter((r) => r.status === "completado").length },
              { id: "apuntado", label: "Apuntado", count: records.filter((r) => r.status === "apuntado").length },
              { id: "todos", label: "Todos", count: records.length },
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>

        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar en mis cursos…" icon="Search" style={{ flex: 1, maxWidth: 320, minWidth: 200 }} />
          <Button variant="ghost" size="sm" icon="Filter">Categoría</Button>
          <Button variant="ghost" size="sm" icon="Filter">Año</Button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{filtered.length} cursos</span>
        </div>

        {/* Cabecera de tabla */}
        <div
          style={{
            display: "grid", gridTemplateColumns: "minmax(280px, 2.4fr) 1fr 1.2fr 1.1fr 0.9fr 120px",
            padding: "10px 20px", background: "var(--kr-neutral-50)", borderBottom: "1px solid var(--kr-border)",
            fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, color: "var(--kr-fg-muted)",
          }}
        >
          <span>Curso</span>
          <span>Categoría</span>
          <span>Progreso</span>
          <span>Última actividad</span>
          <span>Apuntado</span>
          <span style={{ textAlign: "right" }}>Acciones</span>
        </div>

        {filtered.length === 0 && (
          <div style={{ padding: 36, textAlign: "center", color: "var(--kr-fg-muted)", fontSize: 13 }}>
            No hay cursos en este filtro.
          </div>
        )}

        {filtered.map((r, i) => (
          <HistRow key={`${r.kind}-${r.course.id}`} rec={r} last={i === filtered.length - 1} onOpen={() => r.kind === "course" && onOpenCourse(r.course.id)} />
        ))}
      </Card>

      {/* Tira de certificados */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Mis certificados</div>
            <div style={{ marginTop: 4, fontSize: 18, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{certs} certificados emitidos · 2025–2026</div>
          </div>
          <Button variant="ghost" size="sm" iconRight="ArrowRight">Ver todos</Button>
        </div>
        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }} data-responsive-grid="3">
          {records.filter((r) => r.cert).slice(0, 3).map((r) => (
            <CertCard key={r.course.id} rec={r} />
          ))}
        </div>
      </Card>
    </div>
  );
}

function KpiH({ label, value, suffix, tone }) {
  const fg = {
    success: "var(--kr-success-700)",
    brand: "var(--kr-action-soft-fg)",
    warning: "var(--kr-warning-700)",
  }[tone] || "var(--kr-fg-strong)";
  return (
    <Card>
      <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 8 }}>
        <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 28, fontWeight: 700, color: fg, letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</span>
        {suffix && <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>{suffix}</span>}
      </div>
    </Card>
  );
}

function HistRow({ rec, last, onOpen }) {
  const c = rec.course;
  const [hover, setHover] = useState(false);

  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "grid", gridTemplateColumns: "minmax(280px, 2.4fr) 1fr 1.2fr 1.1fr 0.9fr 120px",
        gap: 14, alignItems: "center",
        padding: "12px 20px", borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover ? "var(--kr-bg-subtle)" : "var(--kr-bg-surface)",
        cursor: rec.kind === "course" ? "pointer" : "default", fontSize: 13,
      }}
    >
      {/* Título + miniatura */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <div style={{ width: 72, flexShrink: 0, borderRadius: 6, overflow: "hidden", border: "1px solid var(--kr-border-subtle)" }}>
          <CourseThumb course={c} size="sm" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ color: "var(--kr-fg-strong)", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</span>
            {c.mandatory && <Badge tone="warning" dot>Obligatorio</Badge>}
          </div>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 3, display: "flex", gap: 8 }}>
            <span>{c.instructor}</span>
            <span>·</span>
            <span style={{ fontFamily: "var(--kr-font-mono)" }}>{fmtMinutes(c.durationMin)}</span>
            {c.volumes && c.volumes.length > 0 && <><span>·</span><span style={{ fontFamily: "var(--kr-font-mono)" }}>{totalVolumes(c)} vol</span></>}
          </div>
        </div>
      </div>

      <div style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>{c.category}</div>

      <div>
        {rec.status === "completado" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="CheckCircle2" size={16} style={{ color: "var(--kr-success-500)" }} />
            <span style={{ fontSize: 12, fontFamily: "var(--kr-font-mono)", color: "var(--kr-success-700)", fontWeight: 600 }}>100%</span>
          </div>
        ) : rec.status === "apuntado" ? (
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Sin empezar</div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 80 }}>
              <ProgressBar value={rec.progress} />
            </div>
            <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-strong)", fontWeight: 600 }}>{Math.round(rec.progress * 100)}%</span>
          </div>
        )}
      </div>

      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-strong)" }}>{rec.lastAt}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-muted)" }}>{rec.enrolled}</div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
        {rec.cert && <IconBtn icon="Award" title="Ver certificado" />}
        {rec.status !== "completado" && rec.kind === "course" && (
          <Button variant="soft" size="sm" iconRight="ArrowRight" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
            {rec.status === "apuntado" ? "Empezar" : "Continuar"}
          </Button>
        )}
        {rec.status === "completado" && <IconBtn icon="RefreshCw" title="Repasar" />}
      </div>
    </div>
  );
}

function CertCard({ rec }) {
  const c = rec.course;
  return (
    <div style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 8, padding: 14, display: "flex", gap: 12, alignItems: "center", background: "var(--kr-neutral-25)" }}>
      <div style={{ width: 44, height: 44, borderRadius: 8, background: "linear-gradient(135deg, var(--kr-warning-500), var(--kr-warning-700))", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name="Award" size={22} style={{ color: "#fff" }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</div>
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>Emitido el {rec.completedOn} · {c.category}</div>
      </div>
      <IconBtn icon="Download" title="Descargar PDF" />
    </div>
  );
}

export default FormacionHistorial;
